import React, {
  Fragment,
  FunctionComponent,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { v1 as uuidv1 } from 'uuid';
import BasicInput from '@empiriecom/module-components/FormControls/Input/BasicInput';
import Label from '@empiriecom/module-components/FormControls/Label';
import { Dimension, SelectionRange } from '@/interfaces';
import { useMadeToMeasure } from '@/src/components/ProductContext';
import { InputError } from './types';

const messages = defineMessages({
  dimensionHeadline: {
    defaultMessage: 'Wunschmaße',
    id: 'made-to-measure.dimension.headline',
  },
  inputPlaceholder: {
    defaultMessage: '{displayText} in {step}{unit} Schritten',
    id: 'made-to-measure.input-placeholder',
  },
});

interface InputProps {
  dimension: Dimension;
  onChange?: (value: number) => void;
}

interface FieldProps {
  axisType: string;
  displayText: string;
  range: SelectionRange;
  onChange?: (value: number) => void;
}

const InputContainer = styled.div`
  margin-bottom: 0.75rem;
`;

const Field: FunctionComponent<FieldProps> = ({
  axisType,
  displayText,
  range,
  onChange,
}): ReactElement => {
  const { max, min, step, unit } = range;
  const [error, setError] = useState<InputError>(null);
  const [stateValue, setStateValue] = useState<string>('');
  const { setLengthInputRef, setWidthInputRef, lengthError, widthError } = useMadeToMeasure();
  const isFirstRender = useRef(true);
  const intl = useIntl();
  const inputId = uuidv1();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    switch (axisType) {
      case 'LENGTH':
        setError(lengthError);
        break;
      case 'WIDTH':
        setError(widthError);
        break;
      default:
    }
  }, [lengthError, widthError]);

  useEffect(() => {
    if (isFirstRender.current) {
      switch (axisType) {
        case 'LENGTH':
          setLengthInputRef(inputRef);
          break;
        case 'WIDTH':
          setWidthInputRef(inputRef);
          break;
        default:
      }
      isFirstRender.current = false;
    }
  }, []);

  if (range) {
    return (
      <div data-testid={`input-wrapper-${error !== null ? error.type : 'valid'}`}>
        <Label htmlFor={inputId}>
          <FormattedMessage
            defaultMessage="{displayText} ({min}-{max}{unit})"
            id="product-selection.made-to-measure"
            values={{ displayText, max, min, unit }}
          />
        </Label>
        <BasicInput
          error={error?.message}
          id={inputId}
          onChange={(event) => {
            setStateValue(event.target.value);
            if (typeof onChange === 'function') {
              onChange((event.target.value as unknown) as number);
            }
          }}
          placeholder={intl.formatMessage(messages.inputPlaceholder, { displayText, step, unit })}
          inputRef={inputRef}
          step="any"
          type="number"
          value={stateValue}
        />
      </div>
    );
  }
  return null;
};

export const Input: FunctionComponent<InputProps> = ({
  dimension: { displayName, values, axisType },
  onChange,
}): ReactElement => {
  // const intl = useIntl();
  const container = (
    <InputContainer>
      {/* <Header dimensionName={intl.formatMessage(messages.dimensionHeadline)} /> */}
      <div data-testid="product-selection-inputs">
        {values.map(({ range }) => (
          <Fragment key={displayName}>
            {range && (
              <Field
                axisType={axisType}
                displayText={displayName}
                onChange={onChange}
                range={range}
              />
            )}
          </Fragment>
        ))}
      </div>
    </InputContainer>
  );
  return container;
};

export default Input;
